'use client';

import { create } from 'zustand';
import type { Id } from 'convex/_generated/dataModel';
import { toISODate } from '@shared/dates';
import type { MeasureType, MeasureUnit, WeightUnit } from '@modules/harvest/business_logic';

interface HarvestStore {
  selectedGardenId: Id<'gardens'> | null;
  editDialogOpen: boolean;
  editingHarvestId: Id<'harvests'> | null;
  editPlantId: string;
  editHarvestDate: string;
  editMeasureUnit: MeasureUnit;
  editAmount: string;
  quickAddPlantId: string;
  quickAddAmount: string;
  quickAddMeasureUnit: MeasureUnit;
  quickAddDate: string;
  quickAddShowDate: boolean;
  deleteDialogOpen: boolean;
  harvestToDelete: Id<'harvests'> | null;
  isSaving: boolean;

  setSelectedGardenId: (gardenId: Id<'gardens'> | null) => void;
  setIsSaving: (isSaving: boolean) => void;
  startEdit: (harvest: {
    _id: Id<'harvests'>;
    plantId: string;
    harvestDate: string;
    measureType: MeasureType;
    amount: number;
    weightUnit?: WeightUnit;
  }) => void;
  cancelEdit: () => void;
  setEditPlantId: (plantId: string) => void;
  setEditHarvestDate: (date: string) => void;
  setEditMeasureUnit: (unit: MeasureUnit) => void;
  setEditAmount: (amount: string) => void;
  setQuickAddPlantId: (plantId: string) => void;
  setQuickAddAmount: (amount: string) => void;
  setQuickAddMeasureUnit: (unit: MeasureUnit) => void;
  setQuickAddDate: (date: string) => void;
  toggleQuickAddShowDate: () => void;
  resetQuickAdd: () => void;
  confirmDelete: (harvestId: Id<'harvests'>) => void;
  cancelDelete: () => void;
}

const today = () => toISODate(new Date());

const defaultQuickAdd = {
  quickAddPlantId: '',
  quickAddAmount: '',
  quickAddMeasureUnit: 'count' as MeasureUnit,
  quickAddDate: today(),
  quickAddShowDate: false,
};

const defaultEdit = {
  editDialogOpen: false,
  editingHarvestId: null as Id<'harvests'> | null,
  editPlantId: '',
  editHarvestDate: today(),
  editMeasureUnit: 'count' as MeasureUnit,
  editAmount: '',
};

export const useHarvestStore = create<HarvestStore>((set) => ({
  selectedGardenId: null,
  deleteDialogOpen: false,
  harvestToDelete: null,
  isSaving: false,
  ...defaultQuickAdd,
  ...defaultEdit,

  setSelectedGardenId: (gardenId) => set({ selectedGardenId: gardenId }),
  setIsSaving: (isSaving) => set({ isSaving }),

  startEdit: (harvest) =>
    set({
      editDialogOpen: true,
      editingHarvestId: harvest._id,
      editPlantId: harvest.plantId,
      editHarvestDate: harvest.harvestDate,
      editMeasureUnit: harvest.measureType === 'quantity' ? 'count' : (harvest.weightUnit ?? 'g'),
      editAmount: String(harvest.amount),
    }),

  cancelEdit: () => set({ ...defaultEdit }),

  setEditPlantId: (plantId) => set({ editPlantId: plantId }),
  setEditHarvestDate: (date) => set({ editHarvestDate: date }),
  setEditMeasureUnit: (unit) => set({ editMeasureUnit: unit }),
  setEditAmount: (amount) => set({ editAmount: amount }),

  setQuickAddPlantId: (plantId) => set({ quickAddPlantId: plantId }),
  setQuickAddAmount: (amount) => set({ quickAddAmount: amount }),
  setQuickAddMeasureUnit: (unit) => set({ quickAddMeasureUnit: unit }),
  setQuickAddDate: (date) => set({ quickAddDate: date }),
  toggleQuickAddShowDate: () => set((s) => ({ quickAddShowDate: !s.quickAddShowDate })),
  resetQuickAdd: () => set({ ...defaultQuickAdd, quickAddDate: today() }),

  confirmDelete: (harvestId) => set({ harvestToDelete: harvestId, deleteDialogOpen: true }),
  cancelDelete: () => set({ harvestToDelete: null, deleteDialogOpen: false }),
}));
